import { useState, useCallback, useMemo } from 'react';
import { getProjectsSummary } from '../../services/api/reportsServices';
import useAppSnackbar from '../useAppSnackbar';
import { ProjectsSummary, ReportFilters } from '../../models/reports';
import { DEFAULT_PAGE_SIZE } from '../../constants';

interface GetProjectsSummaryReturn {
  items: ProjectsSummary[];
  isLoading: boolean;
  fetchData: (payload: ReportFilters, loggedHoursProjecs: boolean) => void;
  isEmpty: boolean;
  hasMore: boolean;
  isFetching: boolean;
}

const defaultPayloadData = {
  skip: null,
  take: DEFAULT_PAGE_SIZE,
  filter: null,
  includeInactive: true,
  includeDeleted: true,
};

export function useProjectsSummary(): GetProjectsSummaryReturn {
  const [items, setItems] = useState<ProjectsSummary[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const { onErrorSnackbar } = useAppSnackbar();

  const fetchData = useCallback(
    (payloadParam: ReportFilters, loggedHoursProjecs) => {
      if (!payloadParam.skip) {
        setIsLoading(true);
      } else {
        setIsFetching(true);
      }

      getProjectsSummary(
        { ...defaultPayloadData, ...payloadParam },
        loggedHoursProjecs
      )
        .then((resp) => {
          if (payloadParam.skip) {
            setItems((prevState) => [...prevState, ...resp.items]);
          } else {
            setItems([...resp.items]);
          }

          const doesHaveMore =
            resp.totalCount - resp.items.length - (payloadParam.skip || 0) > 0;

          setHasMore(doesHaveMore);
        })
        .catch(onErrorSnackbar)
        .finally(() => {
          setIsLoading(false);
          setIsFetching(false);
        });
    },
    [onErrorSnackbar]
  );

  const isEmpty = useMemo(
    () => !items.length && !isLoading,
    [items.length, isLoading]
  );

  return {
    items,
    isLoading,
    isFetching,
    isEmpty,
    hasMore,
    fetchData,
  };
}
